import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './AdminPanel.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin`;

const AdminPanel = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [adminToken, setAdminToken] = useState(localStorage.getItem('adminToken') || '');
  const [loginData, setLoginData] = useState({ username: '', password: '' });
  const [loginError, setLoginError] = useState('');
  const [activeTab, setActiveTab] = useState('stats');
  const [loading, setLoading] = useState(false);
  
  // Data states
  const [stats, setStats] = useState(null);
  const [blocklist, setBlocklist] = useState([]);
  const [texts, setTexts] = useState([]);
  const [pendingModeration, setPendingModeration] = useState([]);
  const [settings, setSettings] = useState({
    payment_providers: {
      stripe_enabled: true,
      whop_enabled: true,
      stripe_order: 1,
      whop_order: 2
    }
  });
  const [freeCandleSettings, setFreeCandleSettings] = useState({
    enabled: true,
    daily_limit: 5,
    monthly_limit: 30,
    duration_days: 10,
    extension_cycles: 0,
    reminder_days: 3,
    discount_small: 0,
    discount_medium: 0,
    discount_large: 0
  });
  
  // Form states
  const [newTerm, setNewTerm] = useState({ term: '', category: 'general' });
  const [newText, setNewText] = useState({ key: '', language: 'de', value: '' });
  const [selectedLanguage, setSelectedLanguage] = useState('de');
  const [importData, setImportData] = useState('');
  const [importing, setImporting] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  
  const navigate = useNavigate();

  useEffect(() => {
    if (adminToken) {
      verifySession();
    }
  }, []);

  useEffect(() => {
    if (isLoggedIn) {
      loadData();
    }
  }, [isLoggedIn, activeTab]);

  const verifySession = async () => {
    try {
      await axios.get(`${API}/stats`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setIsLoggedIn(true);
    } catch {
      localStorage.removeItem('adminToken');
      setAdminToken('');
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    try {
      const response = await axios.post(`${API}/login`, loginData);
      setAdminToken(response.data.token);
      localStorage.setItem('adminToken', response.data.token);
      setIsLoggedIn(true);
    } catch (err) {
      setLoginError(err.response?.data?.detail || 'Login fehlgeschlagen');
    }
  };

  const handleLogout = async () => {
    await axios.post(`${API}/logout`, {}, {
      headers: { Authorization: `Bearer ${adminToken}` }
    }).catch(() => {});
    localStorage.removeItem('adminToken');
    setAdminToken('');
    setIsLoggedIn(false);
  };

  const loadData = async () => {
    setLoading(true);
    const headers = { Authorization: `Bearer ${adminToken}` };
    
    try {
      if (activeTab === 'stats') {
        const res = await axios.get(`${API}/stats`, { headers });
        setStats(res.data);
      } else if (activeTab === 'blocklist') {
        const res = await axios.get(`${API}/blocklist`, { headers });
        setBlocklist(res.data);
      } else if (activeTab === 'texts') {
        const res = await axios.get(`${API}/texts/all-labels?language=${selectedLanguage}`, { headers });
        setTexts(res.data);
      } else if (activeTab === 'moderation') {
        const res = await axios.get(`${API}/moderation/pending`, { headers });
        setPendingModeration(res.data);
      } else if (activeTab === 'settings') {
        const res = await axios.get(`${API}/settings`, { headers });
        setSettings(res.data);
        // Also load free candle settings
        const freeRes = await axios.get(`${API}/settings/free-candles`, { headers });
        setFreeCandleSettings(freeRes.data);
      }
    } catch (err) {
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  const addBlocklistTerm = async () => {
    if (!newTerm.term.trim()) return;
    try {
      await axios.post(`${API}/blocklist`, newTerm, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setNewTerm({ term: '', category: 'general' });
      loadData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Fehler');
    }
  };

  const deleteBlocklistTerm = async (termId) => {
    if (!window.confirm('Begriff wirklich löschen?')) return;
    try {
      await axios.delete(`${API}/blocklist/${termId}`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      loadData();
    } catch (err) {
      alert('Fehler beim Löschen');
    }
  };

  const saveText = async () => {
    if (!newText.key.trim() || !newText.value.trim()) return;
    try {
      await axios.post(`${API}/texts`, newText, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setNewText({ key: '', language: selectedLanguage, value: '' });
      loadData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Fehler');
    }
  };

  const deleteText = async (textId) => {
    if (!window.confirm('Text wirklich löschen?')) return;
    try {
      await axios.delete(`${API}/texts/${textId}`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      loadData();
    } catch (err) {
      alert('Fehler beim Löschen');
    }
  };

  const exportTexts = async () => {
    try {
      const res = await axios.get(`${API}/texts/export/${selectedLanguage}`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      const dataStr = JSON.stringify(res.data, null, 2);
      const blob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `texts_${selectedLanguage}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert('Export fehlgeschlagen');
    }
  };

  const syncDefaultLabels = async () => {
    try {
      const res = await axios.post(`${API}/texts/sync-defaults`, {}, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      alert(res.data.message);
      loadData();
    } catch (err) {
      alert('Sync fehlgeschlagen: ' + (err.response?.data?.detail || err.message));
    }
  };

  const importTexts = async () => {
    if (!importData.trim()) return;
    setImporting(true);
    try {
      // Parse CSV: key;language;value
      const lines = importData.trim().split('\n');
      const textsToImport = [];
      
      for (const line of lines) {
        const parts = line.split(';');
        if (parts.length >= 3) {
          textsToImport.push({
            key: parts[0].trim(),
            language: parts[1].trim(),
            value: parts.slice(2).join(';').trim() // Allow semicolons in value
          });
        }
      }
      
      if (textsToImport.length === 0) {
        alert('Keine gültigen Daten gefunden. Format: key;language;value');
        return;
      }
      
      const res = await axios.post(`${API}/texts/bulk-import`, 
        { texts: textsToImport },
        { headers: { Authorization: `Bearer ${adminToken}` }}
      );
      
      alert(`Import erfolgreich!\n${res.data.imported} neu, ${res.data.updated} aktualisiert`);
      setImportData('');
      loadData();
    } catch (err) {
      alert('Import fehlgeschlagen: ' + (err.response?.data?.detail || err.message));
    } finally {
      setImporting(false);
    }
  };

  const handleModeration = async (itemId, action) => {
    try {
      await axios.post(`${API}/moderation/${itemId}/${action}`, {}, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      loadData();
    } catch (err) {
      alert('Fehler');
    }
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await axios.put(`${API}/settings`, 
        { payment_providers: settings.payment_providers },
        { headers: { Authorization: `Bearer ${adminToken}` }}
      );
      alert('Einstellungen gespeichert!');
    } catch (err) {
      alert('Fehler beim Speichern: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSavingSettings(false);
    }
  };

  const saveFreeCandleSettings = async () => {
    setSavingSettings(true);
    try {
      await axios.put(`${API}/settings/free-candles`, 
        freeCandleSettings,
        { headers: { Authorization: `Bearer ${adminToken}` }}
      );
      alert('Kostenlose Kerzen-Einstellungen gespeichert!');
    } catch (err) {
      alert('Fehler beim Speichern: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSavingSettings(false);
    }
  };

  const updatePaymentProvider = (provider, field, value) => {
    setSettings(prev => ({
      ...prev,
      payment_providers: {
        ...prev.payment_providers,
        [`${provider}_${field}`]: value
      }
    }));
  };

  // Login Screen
  if (!isLoggedIn) {
    return (
      <div className="admin-login-page">
        <div className="admin-login-container">
          <h1>🔐 Admin Panel</h1>
          <p>Eternal Lights Administration</p>
          
          {loginError && <div className="admin-error">{loginError}</div>}
          
          <form onSubmit={handleLogin}>
            <input
              type="text"
              placeholder="Benutzername"
              value={loginData.username}
              onChange={(e) => setLoginData({ ...loginData, username: e.target.value })}
              required
            />
            <input
              type="password"
              placeholder="Passwort"
              value={loginData.password}
              onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
              required
            />
            <button type="submit">Anmelden</button>
          </form>
          
          <button className="admin-back-btn" onClick={() => navigate('/')}>
            ← Zurück zur Website
          </button>
        </div>
      </div>
    );
  }

  // Admin Dashboard
  return (
    <div className="admin-panel">
      <header className="admin-header">
        <h1>🔐 Admin Panel</h1>
        <button onClick={handleLogout}>Abmelden</button>
      </header>

      <nav className="admin-nav">
        <button 
          className={activeTab === 'stats' ? 'active' : ''} 
          onClick={() => setActiveTab('stats')}
        >
          📊 Übersicht
        </button>
        <button 
          className={activeTab === 'blocklist' ? 'active' : ''} 
          onClick={() => setActiveTab('blocklist')}
        >
          🚫 Blocklist
        </button>
        <button 
          className={activeTab === 'texts' ? 'active' : ''} 
          onClick={() => setActiveTab('texts')}
        >
          📝 Texte
        </button>
        <button 
          className={activeTab === 'moderation' ? 'active' : ''} 
          onClick={() => setActiveTab('moderation')}
        >
          ⚠️ Moderation {pendingModeration.length > 0 && `(${pendingModeration.length})`}
        </button>
        <button 
          className={activeTab === 'settings' ? 'active' : ''} 
          onClick={() => setActiveTab('settings')}
        >
          ⚙️ Einstellungen
        </button>
      </nav>

      <main className="admin-content">
        {loading && <div className="admin-loading">Lädt...</div>}

        {/* Stats Tab */}
        {activeTab === 'stats' && stats && (
          <div className="admin-stats">
            <h2>Dashboard</h2>
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-value">{stats.total_candles}</div>
                <div className="stat-label">Kerzen gesamt</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">{stats.total_users}</div>
                <div className="stat-label">Registrierte Nutzer</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">{stats.blocklist_terms}</div>
                <div className="stat-label">Blocklist-Begriffe</div>
              </div>
              <div className="stat-card warning">
                <div className="stat-value">{stats.pending_moderation}</div>
                <div className="stat-label">Offene Moderationen</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">{stats.app_texts}</div>
                <div className="stat-label">App-Texte</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">{stats.languages?.length || 0}</div>
                <div className="stat-label">Sprachen</div>
              </div>
            </div>
          </div>
        )}

        {/* Blocklist Tab */}
        {activeTab === 'blocklist' && (
          <div className="admin-blocklist">
            <h2>Blocklist verwalten</h2>
            <p className="admin-hint">
              Begriffe auf der Blocklist werden automatisch bei Kerzen-Widmungen blockiert.
            </p>
            
            <div className="add-form">
              <input
                type="text"
                placeholder="Neuer Begriff"
                value={newTerm.term}
                onChange={(e) => setNewTerm({ ...newTerm, term: e.target.value })}
              />
              <select
                value={newTerm.category}
                onChange={(e) => setNewTerm({ ...newTerm, category: e.target.value })}
              >
                <option value="general">Allgemein</option>
                <option value="historical">Historisch</option>
                <option value="hate">Hassrede</option>
                <option value="extremist">Extremismus</option>
              </select>
              <button onClick={addBlocklistTerm}>+ Hinzufügen</button>
            </div>

            <table className="admin-table">
              <thead>
                <tr>
                  <th>Begriff</th>
                  <th>Kategorie</th>
                  <th>Hinzugefügt von</th>
                  <th>Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {blocklist.map((term) => (
                  <tr key={term.id}>
                    <td><strong>{term.term}</strong></td>
                    <td>
                      <span className={`category-badge ${term.category}`}>
                        {term.category}
                      </span>
                    </td>
                    <td>{term.added_by}</td>
                    <td>
                      <button 
                        className="delete-btn"
                        onClick={() => deleteBlocklistTerm(term.id)}
                      >
                        Löschen
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Texts Tab */}
        {activeTab === 'texts' && (
          <div className="admin-texts">
            <h2>App-Texte verwalten</h2>
            <p className="admin-hint">
              Hier können Sie alle Texte der Website bearbeiten und Übersetzungen hinzufügen.
            </p>

            <div className="language-selector">
              <label>Sprache:</label>
              <select
                value={selectedLanguage}
                onChange={(e) => {
                  setSelectedLanguage(e.target.value);
                  setNewText({ ...newText, language: e.target.value });
                }}
              >
                <option value="de">DE Deutsch</option>
                <option value="en">EN English</option>
                <option value="fr">FR Français</option>
                <option value="es">ES Español</option>
                <option value="it">IT Italiano</option>
                <option value="pt">PT Português</option>
                <option value="ru">RU Русский</option>
                <option value="zh">ZH 中文</option>
                <option value="hi">HI हिन्दी</option>
              </select>
              <button onClick={loadData}>Laden</button>
              <button onClick={exportTexts} className="export-btn">Export JSON</button>
            </div>

            {/* Bulk Import Section */}
            <div className="import-section">
              <h3>Bulk Import (CSV)</h3>
              <p className="admin-hint">Format: key;language;value (eine Zeile pro Text)</p>
              <textarea
                placeholder="hero.title;de;Willkommen&#10;hero.title;en;Welcome&#10;hero.subtitle;de;Untertitel"
                value={importData}
                onChange={(e) => setImportData(e.target.value)}
                rows={5}
              />
              <button 
                onClick={importTexts} 
                disabled={importing || !importData.trim()}
                className="import-btn"
              >
                {importing ? 'Importiert...' : 'CSV Importieren'}
              </button>
              <button 
                onClick={syncDefaultLabels} 
                className="sync-btn"
                title="Fehlende Standard-Labels aus dem Code hinzufuegen"
              >
                Labels synchronisieren
              </button>
            </div>
            
            <div className="add-form text-form">
              <input
                type="text"
                placeholder="Schlüssel (z.B. hero.title)"
                value={newText.key}
                onChange={(e) => setNewText({ ...newText, key: e.target.value })}
              />
              <textarea
                placeholder="Text"
                value={newText.value}
                onChange={(e) => setNewText({ ...newText, value: e.target.value })}
                rows={2}
              />
              <button onClick={saveText}>Speichern</button>
            </div>

            <table className="admin-table">
              <thead>
                <tr>
                  <th>Status</th>
                  <th>Schlüssel</th>
                  <th>Text</th>
                  <th>Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {texts.map((text, idx) => (
                  <tr key={text.id || `missing-${idx}`} className={!text.has_translation ? 'missing-translation' : ''}>
                    <td>
                      {text.has_translation ? (
                        <span className="status-badge translated">✓</span>
                      ) : (
                        <span className="status-badge missing">✗</span>
                      )}
                    </td>
                    <td><code>{text.key}</code></td>
                    <td className={!text.has_translation ? 'missing-text' : ''}>
                      {text.value}
                    </td>
                    <td>
                      <button 
                        className="edit-btn"
                        onClick={() => setNewText({ key: text.key, language: text.language, value: text.has_translation ? text.value : '' })}
                      >
                        {text.has_translation ? 'Bearbeiten' : '+ Übersetzen'}
                      </button>
                      {text.has_translation && text.id && (
                        <button 
                          className="delete-btn"
                          onClick={() => deleteText(text.id)}
                        >
                          Löschen
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="translation-stats">
              <span className="stat-item">
                ✓ {texts.filter(t => t.has_translation).length} übersetzt
              </span>
              <span className="stat-item missing">
                ✗ {texts.filter(t => !t.has_translation).length} fehlend
              </span>
              <span className="stat-item total">
                Gesamt: {texts.length}
              </span>
            </div>

            {texts.length === 0 && (
              <div className="empty-state">
                <p>Noch keine Texte für diese Sprache definiert.</p>
                <p>Beispiel-Schlüssel: <code>hero.title</code>, <code>hero.subtitle</code>, <code>button.submit</code></p>
              </div>
            )}
          </div>
        )}

        {/* Moderation Tab */}
        {activeTab === 'moderation' && (
          <div className="admin-moderation">
            <h2>Offene Moderationen</h2>
            <p className="admin-hint">
              Kerzen, die von der KI markiert wurden und manuelle Prüfung benötigen.
            </p>

            {pendingModeration.length === 0 ? (
              <div className="empty-state">
                <p>✅ Keine offenen Moderationen</p>
              </div>
            ) : (
              <div className="moderation-list">
                {pendingModeration.map((item) => (
                  <div key={item.id} className="moderation-item">
                    <div className="moderation-content">
                      <h4>{item.name}</h4>
                      {item.message && <p>"{item.message}"</p>}
                      {item.ai_reason && (
                        <div className="ai-reason">
                          <strong>KI-Begründung:</strong> {item.ai_reason}
                        </div>
                      )}
                    </div>
                    <div className="moderation-actions">
                      <button 
                        className="approve-btn"
                        onClick={() => handleModeration(item.id, 'approve')}
                      >
                        ✓ Freigeben
                      </button>
                      <button 
                        className="reject-btn"
                        onClick={() => handleModeration(item.id, 'reject')}
                      >
                        ✗ Ablehnen
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <div className="admin-settings">
            <h2>Einstellungen</h2>
            
            <div className="settings-section">
              <h3>💳 Zahlungsanbieter</h3>
              <p className="admin-hint">
                Aktivieren oder deaktivieren Sie Zahlungsanbieter für den Checkout.
              </p>
              
              <div className="payment-settings-grid">
                {/* Stripe Settings */}
                <div className="payment-provider-card">
                  <div className="provider-header">
                    <span className="provider-icon">💳</span>
                    <span className="provider-name">Stripe</span>
                  </div>
                  <div className="provider-controls">
                    <label className="toggle-label">
                      <input
                        type="checkbox"
                        checked={settings.payment_providers?.stripe_enabled ?? true}
                        onChange={(e) => updatePaymentProvider('stripe', 'enabled', e.target.checked)}
                      />
                      <span className="toggle-slider"></span>
                      <span className="toggle-text">
                        {settings.payment_providers?.stripe_enabled ? 'Aktiv' : 'Inaktiv'}
                      </span>
                    </label>
                    <div className="order-control">
                      <label>Reihenfolge:</label>
                      <input
                        type="number"
                        min="1"
                        max="10"
                        value={settings.payment_providers?.stripe_order ?? 1}
                        onChange={(e) => updatePaymentProvider('stripe', 'order', parseInt(e.target.value))}
                      />
                    </div>
                  </div>
                  <p className="provider-hint">Kreditkarte, Debitkarte</p>
                </div>

                {/* Whop Settings */}
                <div className="payment-provider-card">
                  <div className="provider-header">
                    <span className="provider-icon">🛒</span>
                    <span className="provider-name">Whop</span>
                  </div>
                  <div className="provider-controls">
                    <label className="toggle-label">
                      <input
                        type="checkbox"
                        checked={settings.payment_providers?.whop_enabled ?? true}
                        onChange={(e) => updatePaymentProvider('whop', 'enabled', e.target.checked)}
                      />
                      <span className="toggle-slider"></span>
                      <span className="toggle-text">
                        {settings.payment_providers?.whop_enabled ? 'Aktiv' : 'Inaktiv'}
                      </span>
                    </label>
                    <div className="order-control">
                      <label>Reihenfolge:</label>
                      <input
                        type="number"
                        min="1"
                        max="10"
                        value={settings.payment_providers?.whop_order ?? 2}
                        onChange={(e) => updatePaymentProvider('whop', 'order', parseInt(e.target.value))}
                      />
                    </div>
                  </div>
                  <p className="provider-hint">Kreditkarte, Apple Pay, Google Pay</p>
                </div>
              </div>

              <button 
                className="save-settings-btn"
                onClick={saveSettings}
                disabled={savingSettings}
              >
                {savingSettings ? 'Speichert...' : '💾 Einstellungen speichern'}
              </button>
            </div>

            {/* Free Candle Settings */}
            <div className="settings-section">
              <h3>🕯️ Kostenlose Kerzen</h3>
              <p className="admin-hint">
                Einstellungen für die kostenlose Kerzen-Funktion.
              </p>
              
              <div className="free-candle-settings-grid">
                <label className="toggle-label">
                  <input
                    type="checkbox"
                    checked={freeCandleSettings.enabled}
                    onChange={(e) => setFreeCandleSettings({...freeCandleSettings, enabled: e.target.checked})}
                  />
                  <span className="toggle-slider"></span>
                  <span className="toggle-text">
                    {freeCandleSettings.enabled ? 'Aktiviert' : 'Deaktiviert'}
                  </span>
                </label>

                <div className="settings-row">
                  <div className="setting-item">
                    <label>Tageslimit (pro Benutzer):</label>
                    <input
                      type="number"
                      min="1"
                      max="100"
                      value={freeCandleSettings.daily_limit}
                      onChange={(e) => setFreeCandleSettings({...freeCandleSettings, daily_limit: parseInt(e.target.value)})}
                    />
                  </div>
                  <div className="setting-item">
                    <label>Monatslimit (pro Benutzer):</label>
                    <input
                      type="number"
                      min="1"
                      max="1000"
                      value={freeCandleSettings.monthly_limit}
                      onChange={(e) => setFreeCandleSettings({...freeCandleSettings, monthly_limit: parseInt(e.target.value)})}
                    />
                  </div>
                </div>

                <div className="settings-row">
                  <div className="setting-item">
                    <label>Brenndauer (Tage):</label>
                    <input
                      type="number"
                      min="1"
                      max="365"
                      value={freeCandleSettings.duration_days}
                      onChange={(e) => setFreeCandleSettings({...freeCandleSettings, duration_days: parseInt(e.target.value)})}
                    />
                  </div>
                  <div className="setting-item">
                    <label>Verlängerungszyklen (0=unbegrenzt):</label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={freeCandleSettings.extension_cycles}
                      onChange={(e) => setFreeCandleSettings({...freeCandleSettings, extension_cycles: parseInt(e.target.value)})}
                    />
                  </div>
                </div>

                <div className="settings-row">
                  <div className="setting-item">
                    <label>Erinnerung (Tage vor Ablauf):</label>
                    <input
                      type="number"
                      min="1"
                      max="30"
                      value={freeCandleSettings.reminder_days}
                      onChange={(e) => setFreeCandleSettings({...freeCandleSettings, reminder_days: parseInt(e.target.value)})}
                    />
                  </div>
                </div>

                <h4>Upgrade-Rabatte (%)</h4>
                <div className="settings-row">
                  <div className="setting-item">
                    <label>Klein (2€):</label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={freeCandleSettings.discount_small}
                      onChange={(e) => setFreeCandleSettings({...freeCandleSettings, discount_small: parseInt(e.target.value)})}
                    />
                  </div>
                  <div className="setting-item">
                    <label>Mittel (5€):</label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={freeCandleSettings.discount_medium}
                      onChange={(e) => setFreeCandleSettings({...freeCandleSettings, discount_medium: parseInt(e.target.value)})}
                    />
                  </div>
                  <div className="setting-item">
                    <label>Groß (10€):</label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={freeCandleSettings.discount_large}
                      onChange={(e) => setFreeCandleSettings({...freeCandleSettings, discount_large: parseInt(e.target.value)})}
                    />
                  </div>
                </div>
              </div>

              <button 
                className="save-settings-btn"
                onClick={saveFreeCandleSettings}
                disabled={savingSettings}
              >
                {savingSettings ? 'Speichert...' : '💾 Kerzen-Einstellungen speichern'}
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminPanel;
